import type { AriaRole, ReactNode } from "react";
import { TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { IconTile } from "./IconTile";

/**
 * A page or a pane given over to one thing the attorney must know: a mark, a heading, a line
 * saying why, and the acts beneath. `ErrorPanel` is it for a failure; on its own it is for a
 * state that offers no report, as a session that ended or a server not reached before sign-in,
 * where no feedback form can open.
 */
export function NoticePanel({
  mark,
  title,
  reason,
  role,
  className,
  children,
}: {
  mark: ReactNode;
  title: string;
  reason: string;
  role?: AriaRole;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div
      role={role}
      className={cn(
        "mx-auto flex max-w-md flex-col items-center gap-3 px-4 py-10 text-center",
        className,
      )}
    >
      {mark}
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <p className="text-sm text-muted-foreground">{reason}</p>
      <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
        {children}
      </div>
    </div>
  );
}

/** The alert tile a failure stands behind. */
export function AlertMark() {
  return (
    <IconTile tone="destructive" size="md" shape="round">
      <TriangleAlert />
    </IconTile>
  );
}

/**
 * What stands in for a page or a pane that could not be shown: an alert tile, what failed, why,
 * Try again where the screen can ask again, the screen's own way on as `children`, and Report
 * this problem, which every error offers.
 */
export function ErrorPanel({
  title,
  reason,
  onRetry,
  onReport,
  className,
  children,
}: {
  title: string;
  reason: string;
  onRetry?: () => void;
  onReport: () => void;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <NoticePanel
      mark={<AlertMark />}
      title={title}
      reason={reason}
      role="alert"
      className={className}
    >
      {onRetry && <Button onClick={onRetry}>Try again</Button>}
      {children}
      <Button variant="secondary" onClick={onReport}>
        Report this problem
      </Button>
    </NoticePanel>
  );
}
